// pages/Market.jsx
// DWI Market — buyer shop: browse products (staples, produce, fish, tech…),
// pick sizes, checkout → pay on the
// existing /pay/c/:chargeId page (DWIPay Wallet or MoMo), then track orders.
// Everything shown (products, photos, sizes, prices, delivery areas) comes
// from Admin → Market. "Price on request" products have no cart — the buyer
// asks DWI (in-app message or WhatsApp). Buyers only ever see DWI's details.
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ZoomIn } from 'lucide-react';
import { marketAPI } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { Button, Card, Input, Select, Badge, Empty, Spinner } from '../components/ui';
import SellTab from './MarketSell';
import RequestsTab from './MarketRequests';
import { useAutoRefresh, mergeMessages, ChatPhoto, AttachPhoto } from './MarketChat';
import { QtyStepper, FloatingCart, CartDrawer, isDesktop, useSavedCart, refreshCart } from '../components/shop/CartControls';
import { PhotoViewer } from '../components/shop/PhotoViewer';

const ugx = n => `UGX ${Number(n || 0).toLocaleString('en-US')}`;
const when = d => (d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

const STATUS = {
  awaiting_payment: ['Waiting for payment', 'amber'],
  paid:             ['Paid — preparing', 'cyan'],
  dispatched:       ['On the way', 'blue'],
  delivered:        ['Delivered', 'teal'],
  completed:        ['Completed', 'green'],
  cancelled:        ['Cancelled', 'gray'],
  expired:          ['Expired (not paid)', 'gray'],
};

export default function Market() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(null);   // { enabled (shop), sellersEnabled, requestsEnabled }
  useEffect(() => { marketAPI.getStatus().then(setStatus).catch(() => setStatus({})); }, []);
  // Shop closed → land on whatever is open: buy requests, then "Sell to DWI".
  const fallback = !status || status.enabled ? 'shop' : status.requestsEnabled ? 'requests' : status.sellersEnabled ? 'sell' : 'shop';
  const tab = ['orders', 'messages', 'sell', 'requests', 'shop'].includes(params.get('tab')) ? params.get('tab') : fallback;
  // [{ productId, variantId, name, unitName, priceUGX, qty, max, min }] — kept in this browser for this account
  const [cart, setCart] = useSavedCart(user ? `uganda:${user._id || user.id}` : null);
  const [contact, setContact] = useState(null);
  useEffect(() => { marketAPI.getContact().then(r => setContact(r.contact)).catch(() => {}); }, []);
  // Phase 5: unread conversations badge on the Messages tab.
  const [unread, setUnread] = useState(0);
  const loadUnread = useCallback(() => marketAPI.unread().then(r => setUnread(r.unread || 0)).catch(() => {}), []);
  useEffect(() => { loadUnread(); }, [loadUnread, tab]);
  useAutoRefresh(loadUnread, 20);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-text-primary text-xl font-bold">DwiSend</h1>
          <p className="text-text-muted text-xs">Staples, produce, fish, electricals and more — from DWI. Pay with DWIPay Wallet or Mobile Money.</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant={tab === 'shop' ? 'primary' : 'secondary'} onClick={() => setParams({ tab: 'shop' })}>
            Shop{cart.length ? ` (${cart.length})` : ''}
          </Button>
          <Button size="sm" variant={tab === 'requests' ? 'primary' : 'secondary'} onClick={() => setParams({ tab: 'requests' })}>Buy requests</Button>
          <Button size="sm" variant={tab === 'orders' ? 'primary' : 'secondary'} onClick={() => setParams({ tab: 'orders' })}>My orders</Button>
          <Button size="sm" variant={tab === 'messages' ? 'primary' : 'secondary'} onClick={() => setParams({ tab: 'messages' })}>Messages{unread ? ` (${unread})` : ''}</Button>
          <Button size="sm" variant={tab === 'sell' ? 'primary' : 'secondary'} onClick={() => setParams({ tab: 'sell' })}>Sell to DWI</Button>
        </div>
      </div>
      {tab === 'shop' && <Shop cart={cart} setCart={setCart} contact={contact} requestsOpen={!!status?.requestsEnabled} />}
      {tab === 'orders' && <MyOrders />}
      {tab === 'messages' && <Messages />}
      {tab === 'sell' && <SellTab contact={contact} />}
      {tab === 'requests' && <RequestsTab contact={contact} />}
      <ContactCard c={contact} />
    </div>
  );
}

// ── Contact DWI — buyers only ever get DWI's details (Admin → Market → Settings)
function ContactCard({ c }) {
  if (!c || (!c.phone && !c.whatsapp)) return null;
  const wa = c.whatsapp && `https://wa.me/${c.whatsapp}?text=${encodeURIComponent('Hello DwiSend, ')}`;
  return (
    <Card>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-text-primary text-sm font-bold">Questions? Talk to DwiSend</div>
          <div className="text-[11px] text-text-muted">Message us here in the app, or reach us directly.</div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {c.phone && <a href={`tel:${c.phone.replace(/\s/g, '')}`}><Button size="sm" variant="secondary">📞 {c.phone}</Button></a>}
          {wa && <a href={wa} target="_blank" rel="noopener noreferrer"><Button size="sm" variant="success">WhatsApp</Button></a>}
        </div>
      </div>
    </Card>
  );
}

// ── Messages — every conversation is between you and DWI Market ─────────────
const TOPIC_LABEL = { order: 'About an order', selling: 'I want to sell to DWI', buying: 'About a buy request', general: 'General question' };

function Messages() {
  const [params, setParams] = useSearchParams();
  const openId  = params.get('thread');
  const orderId = params.get('order');
  const [threads, setThreads] = useState(null);
  const [closed, setClosed]   = useState(false);

  const load = useCallback(async () => {
    try { setThreads((await marketAPI.myThreads()).threads || []); }
    catch (err) { if (err.status === 503) setClosed(true); else toast.error(err.message); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useAutoRefresh(load, 15, !openId);   // the list picks up DWI's new messages too

  const open = id => setParams({ tab: 'messages', thread: id });

  if (closed) return <Card><Empty icon="📦" title="DwiSend is opening soon" /></Card>;
  if (openId) return <ThreadView id={openId} onBack={() => { setParams({ tab: 'messages' }); load(); }} />;
  if (!threads) return <div className="py-16 flex justify-center"><Spinner /></div>;

  return (
    <div className="space-y-3">
      <NewMessage orderId={orderId} orderRef={params.get('ref')} initialTopic={params.get('topic')}
        listingId={params.get('listing')} requestId={params.get('request')}
        initialSubject={params.get('subject')} initialBody={params.get('body')} onSent={t => open(t._id)} />
      {threads.length === 0
        ? <Card><Empty icon="💬" title="No messages yet" description="Questions about an order, or have produce to sell? Write to DwiSend above." /></Card>
        : threads.map(t => (
          <Card key={t._id}>
            <button className="w-full text-left" onClick={() => open(t._id)}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-text-primary text-sm font-semibold truncate">{t.unread && <span className="text-cyan">● </span>}{t.subject}</div>
                  {t.startedBy === 'dwi' && <div className="text-[10px] text-cyan">From DwiSend</div>}
                  <div className="text-[11px] text-text-muted truncate">{t.lastMessage}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[11px] text-text-muted">{when(t.lastMessageAt)}</div>
                  {t.status === 'closed' && <Badge label="Closed" color="gray" />}
                </div>
              </div>
            </button>
          </Card>
        ))}
    </div>
  );
}

function NewMessage({ orderId, orderRef, listingId, requestId, initialTopic, initialSubject, initialBody, onSent }) {
  const [topic, setTopic]   = useState(orderId ? 'order' : ['selling', 'buying'].includes(initialTopic) ? initialTopic : 'general');
  const [subject, setSubj]  = useState(initialSubject || '');
  const [body, setBody]     = useState(initialBody || '');
  const [photo, setPhoto]   = useState('');
  const [sending, setSending] = useState(false);

  const send = async () => {
    setSending(true);
    try {
      const r = await marketAPI.startThread({ topic, orderId: topic === 'order' ? orderId : undefined,
        listingId: topic === 'selling' && listingId ? listingId : undefined,
        requestId: topic === 'buying' && requestId ? requestId : undefined, subject, body, photo: photo || undefined });
      toast.success('Sent to DwiSend');
      setBody(''); setSubj(''); setPhoto('');
      onSent(r.thread);
    } catch (err) { toast.error(err.message); }
    finally { setSending(false); }
  };

  return (
    <Card>
      <div className="text-text-primary text-sm font-bold mb-3">Message DwiSend</div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Select label="About" value={topic} onChange={e => setTopic(e.target.value)}>
          {orderId && <option value="order">{`Order ${orderRef || ''}`.trim()}</option>}
          <option value="general">{TOPIC_LABEL.general}</option>
          <option value="selling">{TOPIC_LABEL.selling}</option>
          {requestId && <option value="buying">{TOPIC_LABEL.buying}</option>}
        </Select>
        {topic !== 'order' && (
          <Input label="Subject (optional)" value={subject} maxLength={120} onChange={e => setSubj(e.target.value)}
            placeholder={topic === 'selling' ? 'e.g. Rice — 2 tonnes available' : 'e.g. Delivery to Wakiso'} />
        )}
      </div>
      <MessageBox value={body} onChange={setBody} placeholder={topic === 'selling'
        ? 'What you have, how much, your price, where it is, and when it is ready.'
        : 'Write your message…'} />
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <Button size="sm" loading={sending} disabled={!body.trim() && !photo} onClick={send}>Send</Button>
        <AttachPhoto photo={photo} onChange={setPhoto} />
      </div>
    </Card>
  );
}

function ThreadView({ id, onBack }) {
  const [t, setT]       = useState(null);
  const [body, setBody] = useState('');
  const [photo, setPhoto] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => { marketAPI.getThread(id).then(r => setT(r.thread)).catch(err => toast.error(err.message)); }, [id]);
  // Phase 5: check for new messages every few seconds while the chat is open.
  useAutoRefresh(async () => {
    if (!t) return;
    const last = t.messages[t.messages.length - 1]?.createdAt;
    const r = await marketAPI.getThread(id, last);
    setT(cur => (cur ? { ...cur, ...r.thread, messages: mergeMessages(cur.messages, r.thread.messages) } : r.thread));
  });

  const send = async () => {
    setSending(true);
    try { setT((await marketAPI.sendMessage(id, body, photo || undefined)).thread); setBody(''); setPhoto(''); }
    catch (err) { toast.error(err.message); }
    finally { setSending(false); }
  };

  if (!t) return <div className="py-16 flex justify-center"><Spinner /></div>;
  return (
    <Card>
      <div className="flex items-center justify-between gap-2 mb-3">
        <div>
          <div className="text-text-primary text-sm font-bold">{t.subject}</div>
          <div className="text-[11px] text-text-muted">{TOPIC_LABEL[t.topic]}{t.orderRef || t.listingRef || t.requestRef ? ` · ${t.orderRef || t.listingRef || t.requestRef}` : ''}{t.status === 'closed' ? ' · closed (writing again reopens it)' : ''} · updates every few seconds</div>
        </div>
        <Button size="sm" variant="ghost" onClick={onBack}>← All messages</Button>
      </div>
      <Conversation messages={t.messages} mine="user" otherName="DwiSend" />
      <MessageBox value={body} onChange={setBody} placeholder="Write a reply…" />
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <Button size="sm" loading={sending} disabled={!body.trim() && !photo} onClick={send}>Send</Button>
        <AttachPhoto photo={photo} onChange={setPhoto} />
      </div>
    </Card>
  );
}

export function Conversation({ messages, mine, otherName, nameOf }) {
  // Keep the newest message in view as the chat refreshes.
  const box = useRef(null);
  useEffect(() => { if (box.current) box.current.scrollTop = box.current.scrollHeight; }, [messages.length]);
  return (
    <div ref={box} className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
      {messages.map(m => {
        const own = m.from === mine;
        return (
          <div key={m._id} className={`flex ${own ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-xl px-3 py-2 text-sm whitespace-pre-wrap break-words ${own
              ? 'bg-cyan/15 text-text-primary' : 'bg-bg-panel border border-border text-text-primary'}`}>
              <div className="text-[10px] text-text-muted mb-0.5">{own ? 'You' : (nameOf ? nameOf(m) : otherName)} · {when(m.createdAt)}</div>
              {m.body}
              {m.photo && <ChatPhoto name={m.photo} />}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function MessageBox({ value, onChange, placeholder }) {
  return (
    <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={3} maxLength={2000}
      className="mt-3 w-full bg-bg-panel border border-border hover:border-border-hi rounded-xl px-4 py-3 text-text-primary text-sm placeholder:text-text-muted outline-none transition-colors focus:border-cyan focus:ring-1 focus:ring-cyan/30" />
  );
}

// ── Shop + checkout ──────────────────────────────────────────────────────────
function Shop({ cart, setCart, contact, requestsOpen }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData]       = useState(null);
  const [closed, setClosed]   = useState(false);
  const [category, setCategory] = useState('');
  const [checkout, setCheckout] = useState({ deliveryZoneId: '', deliveryAddress: '', buyerPhone: user?.phone || '', notes: '' });
  const [placing, setPlacing] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);   // phone: the cart slide-over
  const [flash, setFlash]       = useState(false);   // computer: highlight the side column
  const cartRef = useRef(null);
  const closeCart = useCallback(() => setCartOpen(false), []);

  // A saved cart may be from an earlier visit: re-check it against the shop
  // each time the shop loads (current price, stock, still on sale) and say what changed.
  const checkedFor = useRef(null);
  useEffect(() => {
    if (!data || checkedFor.current === data) return;
    checkedFor.current = data;
    const r = refreshCart(cart, data.products, 'priceUGX');
    if (r.same) return;
    setCart(r.lines);
    if (r.changed) {
      const parts = [
        r.repriced.length && `new price for ${r.repriced.join(', ')}`,
        r.reduced.length && `less stock for ${r.reduced.join(', ')}`,
        r.removed.length && `${r.removed.filter(Boolean).join(', ') || 'an item'} no longer available`,
      ].filter(Boolean);
      toast(`Your cart was updated: ${parts.join('; ')}. Please check it before ordering.`, { icon: '🛒', duration: 7000 });
    }
  }, [data, cart, setCart]);

  useEffect(() => {
    marketAPI.getShop()
      .then(r => setData(r))
      .catch(err => { if (err.status === 503) setClosed(true); else toast.error(err.message); });
  }, []);

  const zones = data?.zones || [];
  const zone = zones.find(z => z._id === checkout.deliveryZoneId);
  const subtotal = cart.reduce((s, l) => s + l.priceUGX * l.qty, 0);
  const total = subtotal + (zone ? zone.feeUGX : 0);

  const products = useMemo(
    () => (data?.products || []).filter(p => !category || p.categoryId === category),
    [data, category],
  );

  // Never more in the cart than "X available" (the server re-checks stock on order).
  const addToCart = (p, v, qty) => {
    const have = cart.find(l => l.variantId === v._id)?.qty || 0;
    const add = Math.min(qty, v.inStock - have);
    if (add < 1) return toast.error(`Only ${v.inStock} available — already in your cart`);
    setCart(c => {
      const i = c.findIndex(l => l.variantId === v._id);
      if (i >= 0) return c.map((l, j) => (j === i ? { ...l, qty: l.qty + add, max: v.inStock } : l));
      return [...c, { productId: p._id, variantId: v._id, name: p.name, unitName: v.unitName, priceUGX: v.priceUGX, qty: add, min: v.minOrderQty || 1, max: v.inStock }];
    });
    toast.success(`Added ${add} × ${p.name} (${v.unitName})${add < qty ? ` — only ${v.inStock} available` : ''}`);
  };
  const setLineQty = (variantId, qty) => setCart(c => c.map(l => (l.variantId === variantId
    ? { ...l, qty: Math.max(l.min || 1, Math.min(l.max ?? qty, qty)) } : l)));

  const openCart = () => {
    if (!isDesktop()) return setCartOpen(true);
    cartRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setFlash(true);
    setTimeout(() => setFlash(false), 900);
  };

  const placeOrder = async () => {
    if (zones.length && !checkout.deliveryZoneId) return toast.error('Choose your delivery area');
    if (!checkout.deliveryAddress.trim()) return toast.error('Enter the delivery address');
    setPlacing(true);
    try {
      // Last look at the shop: if a price, stock level or delivery fee changed
      // since this page loaded, show the buyer the new cart instead of ordering.
      const fresh = await marketAPI.getShop();
      const feeNow = (fresh.zones || []).find(z => z._id === checkout.deliveryZoneId)?.feeUGX;
      if (refreshCart(cart, fresh.products, 'priceUGX').changed || (zone && feeNow !== zone.feeUGX)) {
        if (zone && feeNow !== zone.feeUGX) toast('The delivery fee has changed — please check the total.', { icon: '🚚', duration: 7000 });
        setData(fresh);
        return;
      }
      const r = await marketAPI.placeOrder({
        items: cart.map(l => ({ productId: l.productId, variantId: l.variantId, qty: l.qty })),
        ...checkout,
        deliveryZoneId: checkout.deliveryZoneId || undefined,
      });
      setCart([]);
      setCartOpen(false);
      toast.success(`Order ${r.order.orderRef} placed — complete payment`);
      navigate(r.payUrl);
    } catch (err) { toast.error(err.message); }
    finally { setPlacing(false); }
  };

  if (closed) return <Card><Empty icon="📦" title="DwiSend is opening soon" description="Check back shortly." /></Card>;
  if (!data) return <div className="py-16 flex justify-center"><Spinner /></div>;

  return (
    <div className="grid lg:grid-cols-3 gap-4 items-start">
      <div className="lg:col-span-2 space-y-3">
        {data.categories.length > 1 && (
          <div className="flex gap-2 flex-wrap">
            <Button size="xs" variant={!category ? 'primary' : 'secondary'} onClick={() => setCategory('')}>All</Button>
            {data.categories.map(c => (
              <Button key={c._id} size="xs" variant={category === c._id ? 'primary' : 'secondary'} onClick={() => setCategory(c._id)}>{c.name}</Button>
            ))}
          </div>
        )}
        {products.length === 0
          ? <Card><Empty icon="📦" title="No products available right now" /></Card>
          : products.map(p => <ProductCard key={p._id} product={p} onAdd={addToCart} contact={contact} requestsOpen={requestsOpen} />)}
      </div>

      <CartDrawer open={cartOpen} onClose={closeCart} panelRef={cartRef}>
      <Card className={flash ? 'ring-2 ring-cyan transition' : 'transition'}>
        <h3 className="text-text-primary font-bold text-sm mb-3">Your order</h3>
        {cart.length === 0 ? <p className="text-xs text-text-muted">Nothing added yet.</p> : (
          <div className="space-y-3">
            {cart.map((l, i) => (
              <div key={l.variantId} className="text-sm space-y-1.5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-text-primary truncate">{l.name}</div>
                    <div className="text-[11px] text-text-muted">{l.unitName} · {ugx(l.priceUGX)} each</div>
                  </div>
                  <button className="text-text-muted hover:text-red text-xs" onClick={() => setCart(c => c.filter((_, j) => j !== i))}>Remove</button>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <QtyStepper value={l.qty} min={l.min || 1} max={l.max} onChange={n => setLineQty(l.variantId, n)} lessLabel="One less" moreLabel="One more" />
                  <span className="font-semibold text-text-primary tabular-nums">{ugx(l.priceUGX * l.qty)}</span>
                </div>
                {l.max != null && l.qty >= l.max && <div className="text-[11px] text-amber">All {l.max} available are in your cart</div>}
              </div>
            ))}

            {zones.length > 0 && (
              <Select label="Delivery area" value={checkout.deliveryZoneId} onChange={e => setCheckout(c => ({ ...c, deliveryZoneId: e.target.value }))}>
                <option value="">— choose —</option>
                {zones.map(z => <option key={z._id} value={z._id}>{z.name} — {ugx(z.feeUGX)}{z.notes ? ` (${z.notes})` : ''}</option>)}
              </Select>
            )}
            <div>
              <label className="text-xs font-semibold text-text-secondary">Delivery address</label>
              <textarea rows={2} className="mt-1 w-full bg-bg-elevated border border-border rounded-xl px-3 py-2 text-sm text-text-primary outline-none focus:border-cyan"
                placeholder="School / hostel name, road, landmark" value={checkout.deliveryAddress}
                onChange={e => setCheckout(c => ({ ...c, deliveryAddress: e.target.value }))} />
            </div>
            <Input label="Phone for delivery" value={checkout.buyerPhone} onChange={e => setCheckout(c => ({ ...c, buyerPhone: e.target.value }))} placeholder="07XXXXXXXX" />
            <Input label="Notes (optional)" value={checkout.notes} onChange={e => setCheckout(c => ({ ...c, notes: e.target.value }))} placeholder="e.g. deliver before Friday" />

            <div className="border-t border-border pt-3 space-y-1 text-sm">
              <div className="flex justify-between"><span>Items</span><span>{ugx(subtotal)}</span></div>
              {zones.length > 0 && <div className="flex justify-between"><span>Delivery</span><span>{zone ? ugx(zone.feeUGX) : '—'}</span></div>}
              <div className="flex justify-between text-text-primary font-bold"><span>Total</span><span>{ugx(total)}</span></div>
            </div>
            <Button className="w-full" loading={placing} onClick={placeOrder}>Place order & pay</Button>
            <p className="text-[11px] text-text-muted text-center">You'll pay on the next screen with DWIPay Wallet or Mobile Money.</p>
          </div>
        )}
      </Card>
      </CartDrawer>
      <FloatingCart count={cart.length} total={ugx(total)} label="Cart" onClick={openCart} />
    </div>
  );
}

const CONDITION_LABEL = { new: 'New', used: 'Used', refurbished: 'Refurbished' };
const dateOnly = d => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

function ProductDetails({ d }) {
  if (!d) return null;
  const bits = [
    d.brand, CONDITION_LABEL[d.condition], d.warranty && `Warranty: ${d.warranty}`, d.origin && `From ${d.origin}`,
    d.perishable && (d.availableUntil ? `Fresh — available until ${dateOnly(d.availableUntil)}` : 'Fresh'),
  ].filter(Boolean);
  if (!bits.length) return null;
  return <div className="flex gap-1 flex-wrap mt-1">{bits.map(b => <span key={b} className="text-[11px] px-2 py-0.5 rounded-full bg-bg-elevated border border-border text-text-secondary">{b}</span>)}</div>;
}

function ProductCard({ product, onAdd, contact, requestsOpen }) {
  const navigate = useNavigate();
  const [photo, setPhoto] = useState(0);
  const [viewing, setViewing] = useState(false);   // full-screen photo viewer
  const [qty, setQty] = useState(() => Object.fromEntries(product.variants.map(v => [v._id, v.minOrderQty || 1])));
  const onRequest = product.priceMode === 'on_request';
  const sizes = product.variants.map(v => v.unitName).join(', ');
  const askText = `Hello DwiSend, I'd like a price for ${product.name} (${sizes}). Quantity I need: `;
  // Phase 3: with buy requests open, "ask for a price" is a buy request DWI answers with a quote.
  const ask = () => navigate(requestsOpen
    ? `/market?tab=requests&ask=1&product=${product._id}&name=${encodeURIComponent(product.name)}&category=${product.categoryId}&unit=${product.variants[0]?.unitId || ''}`
    : `/market?tab=messages&topic=general&subject=${encodeURIComponent(`Price for ${product.name}`)}&body=${encodeURIComponent(askText)}`);

  return (
    <Card>
      <div className="flex gap-4 flex-col sm:flex-row">
        <div className="sm:w-40 flex-shrink-0">
          {product.photos?.length ? (
            <>
              <button type="button" onClick={() => setViewing(true)} aria-label={`Enlarge photo of ${product.name}`} className="relative block w-full cursor-zoom-in">
                <img src={product.photos[photo]} alt={product.name} className="w-full h-32 sm:h-40 rounded-xl object-cover border border-border" />
                <span className="absolute bottom-1.5 right-1.5 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center pointer-events-none"><ZoomIn size={15} /></span>
              </button>
              {product.photos.length > 1 && (
                <div className="flex gap-1 mt-1">
                  {product.photos.map((u, i) => (
                    <button key={u} onClick={() => setPhoto(i)}>
                      <img src={u} alt="" className={`w-8 h-8 rounded object-cover border ${i === photo ? 'border-cyan' : 'border-border'}`} />
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : <div className="w-full h-32 sm:h-40 rounded-xl bg-bg-elevated border border-border flex items-center justify-center text-3xl">📦</div>}
          {viewing && <PhotoViewer photos={product.photos} start={photo} alt={product.name} onClose={() => setViewing(false)} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-text-primary font-bold">{product.name}</div>
          <ProductDetails d={product.details} />
          {product.description && <p className="text-xs text-text-muted mt-1 whitespace-pre-line">{product.description}</p>}
          {onRequest ? (
            <div className="mt-3 space-y-2">
              <div className="text-sm"><Badge label="Price on request" color="amber" /> <span className="text-text-muted text-xs">Sold by: {sizes}</span></div>
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" onClick={ask}>💬 Ask DWI for a price</Button>
                {contact?.whatsapp && (
                  <a href={`https://wa.me/${contact.whatsapp}?text=${encodeURIComponent(askText)}`} target="_blank" rel="noopener noreferrer">
                    <Button size="sm" variant="success">WhatsApp</Button>
                  </a>
                )}
              </div>
            </div>
          ) : (
            <div className="mt-3 space-y-2">
              {product.variants.map(v => {
                const q = Number(qty[v._id]) || 0;
                const out = v.inStock < (v.minOrderQty || 1);
                return (
                  <div key={v._id} className="flex items-center gap-2 flex-wrap">
                    <div className="w-28 text-sm text-text-primary">{v.unitName}</div>
                    <div className="w-32 text-sm font-semibold text-text-primary">{ugx(v.priceUGX)}</div>
                    {out ? <Badge label="Out of stock" color="gray" /> : (
                      <>
                        <input type="number" min={v.minOrderQty || 1} max={v.inStock} step="1" value={qty[v._id]}
                          onChange={e => setQty(s => ({ ...s, [v._id]: e.target.value }))}
                          className="w-20 bg-bg-elevated border border-border rounded-lg px-2 py-1 text-sm text-text-primary outline-none focus:border-cyan" />
                        <Button size="xs" disabled={!Number.isInteger(q) || q < (v.minOrderQty || 1) || q > v.inStock}
                          onClick={() => onAdd(product, v, q)}>Add</Button>
                        <span className="text-[11px] text-text-muted">{v.inStock} available{v.minOrderQty > 1 ? ` · min ${v.minOrderQty}` : ''}</span>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

// ── My orders ────────────────────────────────────────────────────────────────
function MyOrders() {
  const navigate = useNavigate();
  const [orders, setOrders]   = useState(null);
  const [closed, setClosed]   = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await marketAPI.myOrders({ limit: 50 });
      // Just came back from paying? Check unpaid orders against their payment now.
      const waiting = (r.docs || []).filter(o => o.status === 'awaiting_payment');
      if (waiting.length) {
        const synced = await Promise.all(waiting.map(o => marketAPI.syncPayment(o._id).then(x => x.order).catch(() => o)));
        const byId = Object.fromEntries(synced.map(o => [o._id, o]));
        setOrders(r.docs.map(o => byId[o._id] || o));
      } else {
        setOrders(r.docs || []);
      }
    } catch (err) {
      if (err.status === 503) setClosed(true); else toast.error(err.message);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    try { await fn(); toast.success(ok); load(); } catch (err) { toast.error(err.message); }
  };

  if (closed) return <Card><Empty icon="📦" title="DwiSend is opening soon" /></Card>;
  if (!orders) return <div className="py-16 flex justify-center"><Spinner /></div>;
  if (orders.length === 0) return <Card><Empty icon="🧾" title="No orders yet" description="Your orders will show here." /></Card>;

  return (
    <div className="space-y-3">
      {orders.map(o => {
        const [label, color] = STATUS[o.status] || [o.status, 'gray'];
        return (
          <Card key={o._id}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="font-mono text-xs text-cyan">{o.orderRef}</div>
                <div className="text-[11px] text-text-muted">{when(o.createdAt)}</div>
              </div>
              <Badge label={label} color={color} />
            </div>
            <div className="mt-3 text-sm space-y-0.5">
              {o.items.map((i, k) => <div key={k}>{i.qty} × {i.name} ({i.unitName}) — {ugx(i.lineTotalUGX)}</div>)}
              {o.deliveryFeeUGX > 0 && <div className="text-text-muted">Delivery ({o.deliveryZoneName}) — {ugx(o.deliveryFeeUGX)}</div>}
              <div className="text-text-primary font-bold">Total {ugx(o.totalUGX)}</div>
              <div className="text-[11px] text-text-muted">Deliver to: {o.deliveryAddress}</div>
              {o.delivery?.driverName && ['dispatched', 'delivered', 'completed'].includes(o.status) && (
                <div className="text-[11px] text-text-secondary">
                  🚚 {o.delivery.driverName}
                  {o.delivery.driverPhone && <> · <a className="text-cyan" href={`tel:${o.delivery.driverPhone.replace(/\s/g, '')}`}>{o.delivery.driverPhone}</a></>}
                  {(o.delivery.vehicle || o.delivery.plate) && ` · ${[o.delivery.vehicle, o.delivery.plate].filter(Boolean).join(' ')}`}
                </div>
              )}
            </div>
            <div className="mt-3 flex gap-2 flex-wrap">
              {o.status === 'awaiting_payment' && (
                <>
                  <Button size="sm" onClick={() => { window.location.href = `/pay/c/${o.chargeId}`; }}>Pay now</Button>
                  <Button size="sm" variant="secondary" onClick={() => act(() => marketAPI.syncPayment(o._id), 'Payment status checked')}>I've paid — check</Button>
                  <Button size="sm" variant="ghost" onClick={() => act(() => marketAPI.setStatus(o._id, 'cancelled'), 'Order cancelled')}>Cancel</Button>
                </>
              )}
              {o.status === 'dispatched' && (
                <Button size="sm" variant="success" onClick={() => act(() => marketAPI.setStatus(o._id, 'delivered'), 'Thank you — delivery confirmed')}>I received it</Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => navigate(`/market?tab=messages&order=${o._id}&ref=${encodeURIComponent(o.orderRef)}`)}>💬 Message DWI</Button>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
